<template>
  <ZenniZnFilterFilterCategory
    v-if="categories.length"
    :initial-open="initialOpen"
    :title="title"
    :count="totalFilterCount"
  >
    <ul
      v-for="category in computedCategoriesValues"
      :key="category.title"
      class="flex flex-col"
    >
      <li>
        <span class="text-grays-dark pl-4">{{ category.title }}</span>
      </li>
      <ZenniZnListItem
        v-for="{ value, productCount, label, imageurl } in category.facet
          .values"
        :key="value"
        :aria-label="label"
        size="lg"
        class="!h-14"
        @click="toggleSelected(category.facet.name || '', value)"
        @keypress.enter.prevent="
          toggleSelected(category.facet.name || '', value)
        "
        @keypress.space.prevent="
          toggleSelected(category.facet.name || '', value)
        "
      >
        <template #prefix>
          <ZnCheckbox
            role="checkbox"
            tabindex="0"
            :model-value="selectedProxy[category.facet.name]?.includes(value)"
            class="pointer-events-none flex items-center p-1"
            :aria-label="label"
            :aria-labelledby="title"
            :aria-checked="
              selectedProxy[category.facet.name]?.includes(value) ?? false
            "
          />
        </template>
        <div class="justify-content flex items-center gap-2 pl-1">
          <img
            :src="imageurl"
            :alt="label"
            class="aspect-[116/93] max-w-[64px] object-contain"
          />
          <div class="flex gap-2">
            <span class="pl-1">{{ label }}</span>
            <ZenniZnCounter v-if="productCount">{{
              productCount
            }}</ZenniZnCounter>
          </div>
        </div>
      </ZenniZnListItem>
    </ul>
  </ZenniZnFilterFilterCategory>
</template>
<script setup lang="ts">
import { computed } from '#imports'
import { sumBy } from 'lodash-es'
import {
  WRAP_AROUND_GROUP,
  CAT_EYE_GROUP,
  FULL_RIM_GROUP,
  HALF_RIM_GROUP,
} from '~/data/filter-groups'
import type {
  CheckboxCategoriesItemProps,
  CheckboxCategoriesItemEmits,
  CheckboxCategoriesItem,
} from '~/types/filter'

const emit = defineEmits<CheckboxCategoriesItemEmits>()
const props = defineProps<CheckboxCategoriesItemProps>()

const totalFilterCount = computed(() => {
  return props.categories
    .map((cat) => cat.facet.name)
    .reduce((acc, key) => {
      return acc + (selectedProxy.value[key] || []).length
    }, 0)
})

const toggleSelected = (key: string, value: string) => {
  const values = [...(selectedProxy.value?.[key] || [])]
  const index = values.indexOf(value)
  if (index === -1) {
    values.push(value)
  } else {
    values.splice(index, 1)
  }
  emit('update:selected', { key, value: values })
}

const selectedProxy = computed(() => props.selected)
const transformGroupShapeToArray = (group: string) =>
  group.replace(/\[|\]/g, '').split(',')

const computedCategoriesValues = computed<CheckboxCategoriesItem[]>(() => {
  const WrapAroundGroup = transformGroupShapeToArray(WRAP_AROUND_GROUP.filter)
  const CatEyeGroup = transformGroupShapeToArray(CAT_EYE_GROUP.filter)
  const FullRimGroup = transformGroupShapeToArray(FULL_RIM_GROUP.filter)
  const HalfRimGroup = transformGroupShapeToArray(HALF_RIM_GROUP.filter)
  const rimCategory = props.categories.find((cat) => cat.title === 'Rim')
  const shapeCategory = props.categories.find((cat) => cat.title === 'Shape')

  const WrapAroundIndex = shapeCategory?.facet.values.findIndex(
    (facet) => facet.value === WRAP_AROUND_GROUP.imageFrom,
  )
  const CatEyeIndex = shapeCategory?.facet.values.findIndex(
    (facet) => facet.value === CAT_EYE_GROUP.imageFrom,
  )
  const FullRimIndex = rimCategory?.facet.values.findIndex(
    (facet) => facet.value === FULL_RIM_GROUP.imageFrom,
  )
  const HalfRimIndex = rimCategory?.facet.values.findIndex(
    (facet) => facet.value === HALF_RIM_GROUP.imageFrom,
  )

  const WrapAroundImage =
    shapeCategory?.facet.values[WrapAroundIndex || 0]?.imageurl
  const CatEyeImage = shapeCategory?.facet.values[CatEyeIndex || 0]?.imageurl
  const FullRimImage = rimCategory?.facet.values[FullRimIndex || 0]?.imageurl
  const HalfRimImage = rimCategory?.facet.values[HalfRimIndex || 0]?.imageurl

  const withWrapAround = shapeCategory?.facet.values.filter((facet) =>
    WrapAroundGroup.includes(facet.value),
  )
  const withCatEye = shapeCategory?.facet.values.filter((facet) =>
    CatEyeGroup.includes(facet.value),
  )
  const withFullRim = rimCategory?.facet.values.filter((facet) =>
    FullRimGroup.includes(facet.value),
  )
  const withHalfRim = rimCategory?.facet.values.filter((facet) =>
    HalfRimGroup.includes(facet.value),
  )

  const wrapAround = {
    label: 'Wrap-Around',
    imageurl: WrapAroundImage || '',
    value: WRAP_AROUND_GROUP.filter,
    productCount: sumBy(withWrapAround, 'productCount'),
  }
  const catEye = {
    label: 'Cat-Eye',
    imageurl: CatEyeImage || '',
    value: CAT_EYE_GROUP.filter,
    productCount: sumBy(withCatEye, 'productCount'),
  }
  const fullRim = {
    label: 'Full-Rim',
    imageurl: FullRimImage || '',
    value: FULL_RIM_GROUP.filter,
    productCount: sumBy(withFullRim, 'productCount'),
  }
  const halfRim = {
    label: 'Half-Rim',
    imageurl: HalfRimImage || '',
    value: HALF_RIM_GROUP.filter,
    productCount: sumBy(withHalfRim, 'productCount'),
  }

  const newFacetsCategories = shapeCategory
    ? shapeCategory.facet.values.filter(
        (facet) =>
          !WrapAroundGroup.includes(facet.value) &&
          !CatEyeGroup.includes(facet.value),
      )
    : []
  if (withWrapAround && WrapAroundIndex !== undefined) {
    newFacetsCategories.splice(WrapAroundIndex, 0, wrapAround)
  }
  if (withCatEye && CatEyeIndex !== undefined) {
    newFacetsCategories.splice(CatEyeIndex, 0, catEye)
  }
  // remove the facets that belongs to the group and add the new one
  const newShapeCategory = shapeCategory
    ? {
        title: shapeCategory.title,
        facet: {
          ...shapeCategory.facet,
          values: newFacetsCategories.filter(
            (facet) => (facet.productCount || 0) > 0,
          ),
        },
        selected: shapeCategory.selected || [],
      }
    : null

  const response: CheckboxCategoriesItem[] = []
  if (newShapeCategory) {
    response.push(newShapeCategory)
  }

  const newFacetsRim = rimCategory
    ? rimCategory.facet.values.filter(
        (facet) =>
          !FullRimGroup.includes(facet.value) &&
          !HalfRimGroup.includes(facet.value),
      )
    : []
  if (withFullRim && FullRimIndex !== undefined) {
    newFacetsRim.splice(FullRimIndex, 0, fullRim)
  }
  if (withHalfRim && HalfRimIndex !== undefined) {
    newFacetsRim.splice(HalfRimIndex, 0, halfRim)
  }
  const newRimCategory = rimCategory
    ? {
        title: rimCategory.title,
        facet: {
          ...rimCategory.facet,
          values: newFacetsRim.filter((facet) => (facet.productCount || 0) > 0),
        },
        selected: rimCategory.selected || [],
      }
    : null

  if (newRimCategory) {
    response.push(newRimCategory)
  }

  return response
})
</script>
